<script setup lang="ts">
const props = defineProps<{
  guidelines: Record<string, any>
  brand_color: string
}>()
const activeArea = ref<string | null>(null)
const modelParts = {
  head: 'M 50,15 C 55,15 60,20 60,25 C 60,30 55,35 50,35 C 45,35 40,30 40,25 C 40,20 45,15 50,15',
  torso: `
    M 40,38 L 60,38 L 60,74 L 40,74 Z
    M 38.5,42 L 33.5,42 L 33.5,65 L 38.5,65 Z
    M 61.5,42 L 66.5,42 L 66.5,65 L 61.5,65 Z
  `,
  leftHand: 'M 33.5,65 L 38.5,65 L 38.5,73 L 33.5,70 Z',
  rightHand: 'M 61.5,65 L 66.5,65 L 66.5,70 L 61.5,73 Z',
  leftLeg: 'M 42,75.5 L 47,75.5 L 47,104 L 42,104 Z',
  rightLeg: 'M 53,75.5 L 58,75.5 L 58,104 L 53,104 Z',
  leftShoe: 'M 42,105 L 47,105 L 49,110 L 40,110 Z',
  rightShoe: 'M 53,105 L 58,105 L 60,110 L 51,110 Z'
}
const partGroups = {
  legs: ['leftLeg', 'rightLeg'],
  shoes: ['leftShoe', 'rightShoe'],
  hands: ['leftHand', 'rightHand']
}
const defaultColor = '#2563eb'
const brandColor = computed(() => props.brand_color || defaultColor)
const getPartColor = (part: string): string => {
  return hasGuidelinesForPart(part) ? brandColor.value : '#E5E7EB'
}
const partOpacityLevels = {
  head: 0.5,
  torso: 0.5,
  leftHand: 0.6,
  rightHand: 0.6,
  leftLeg: 0.7,
  rightLeg: 0.7,
  leftShoe: 0.6,
  rightShoe: 0.6
}
const getPartOpacity = (part: string): number => {
  if (!hasGuidelinesForPart(part)) {
    return 1
  }
  if (shouldHighlight(part)) {
    return 1
  }
  return partOpacityLevels[part] || 0.8
}
const getPartGroup = (part: string): string | null => {
  for (const [group, parts] of Object.entries(partGroups)) {
    if (parts.includes(part)) {
      return group
    }
  }
  return null
}
const shouldHighlight = (part: string): boolean => {
  if (activeArea.value === part) return true
  const group = getPartGroup(part)
  if (
    group
    && activeArea.value
    && partGroups[group].includes(activeArea.value)
  ) {
    return true
  }
  return false
}
const guidelineMappings: Record<string, string[]> = {
  head: ['Headwear', 'Accessories', 'Hair'],
  torso: [
    'Shirts',
    'Tops',
    'Suits',
    'Clothing',
    'Tie',
    'Neck',
    'Jackets',
    'Sleeves'
  ],
  leftHand: [],
  rightHand: [],
  leftLeg: ['Pants', 'Trousers', 'Jeans', 'Bottoms'],
  rightLeg: ['Pants', 'Trousers', 'Jeans', 'Bottoms'],
  leftShoe: ['Shoes', 'Footwear', 'Boots'],
  rightShoe: ['Shoes', 'Footwear', 'Boots']
}
const hasGuidelinesForPart = (part: string) => {
  const searchTerms = guidelineMappings[part] || []
  if (searchTerms.length === 0) return false
  return Object.keys(props.guidelines).some(key =>
    searchTerms.some(term => key.includes(term))
  )
}
const getGuidelineForPart = (part: string) => {
  const searchTerms = guidelineMappings[part] || []
  for (const [key, value] of Object.entries(props.guidelines)) {
    if (searchTerms.some(term => key.includes(term))) {
      return {
        title: key,
        content: value,
        isNested: typeof value === 'object'
      }
    }
  }
  return null
}
const formatNestedContent = (content: any) => {
  if (typeof content !== 'object') return []
  return Object.entries(content).map(([key, value]) => ({
    label: key,
    value: value as string
  }))
}
const tooltipPosition = ref({ x: 0, y: 0 })
const handleMouseMove = (event: MouseEvent) => {
  tooltipPosition.value = {
    x: event.clientX + 10,
    y: event.clientY + 10
  }
}
const shirtDecorations = {
  middleLine: 'M 50,38 L 50,74',
  pockets: ['M 53,64 L 57,64 L 57,70 L 53,70 Z']
}
</script>

<template>
  <div class="relative w-full h-full min-h-[300px]">
    <svg
      viewBox="0 0 100 120"
      class="w-full h-full"
      @mousemove="handleMouseMove"
    >
      <g class="figure">
        <path
          v-for="(path, part) in modelParts"
          :key="part"
          :d="path"
          :class="{
            'cursor-pointer': hasGuidelinesForPart(part),
            'cursor-not-allowed': !hasGuidelinesForPart(part)
          }"
          :style="{
            fill: getPartColor(part),
            opacity: getPartOpacity(part),
            transition: 'all 0.2s ease-in-out'
          }"
          @mouseenter="hasGuidelinesForPart(part) ? (activeArea = part) : null"
          @mouseleave="hasGuidelinesForPart(part) ? (activeArea = null) : null"
        />
        <path
          :d="shirtDecorations.middleLine"
          stroke="#FFFFFF"
          stroke-width="0.5"
          fill="none"
          opacity="0.6"
        />
        <path
          v-for="(pocket, index) in shirtDecorations.pockets"
          :key="`pocket-${index}`"
          :d="pocket"
          stroke="#FFFFFF"
          stroke-width="0.5"
          fill="none"
          opacity="0.6"
        />
      </g>
    </svg>
    <div
      v-if="activeArea && getGuidelineForPart(activeArea)"
      class="fixed z-50 bg-white p-4 rounded-lg shadow-lg max-w-xs"
      :style="{
        left: `${tooltipPosition.x}px`,
        top: `${tooltipPosition.y}px`,
        borderTop: `4px solid ${brandColor}`
      }"
    >
      <h4 class="font-medium text-gray-900">
        {{ getGuidelineForPart(activeArea)?.title }}
      </h4>
      <template v-if="getGuidelineForPart(activeArea)?.isNested">
        <ul class="mt-2 space-y-1">
          <li
            v-for="(item, idx) in formatNestedContent(
              getGuidelineForPart(activeArea)?.content
            )"
            :key="idx"
            class="text-sm"
          >
            <span class="font-medium text-gray-700">{{ item.label }}:</span>
            <span class="text-gray-600">{{ item.value }}</span>
          </li>
        </ul>
      </template>
      <template v-else>
        <p class="text-gray-600 text-sm mt-1">
          {{ getGuidelineForPart(activeArea)?.content }}
        </p>
      </template>
    </div>
  </div>
</template>
